import React, { useRef, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { DirectorInformation } from "~/Pages/Directors/types/director";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import DuplicateInformationCard from "~/Pages/Directors/onboarding/components/director-matching-results/DuplicateInformationCard";

interface DirectorMatchingResultProps {
  data: Array<DirectorInformation>;
  loading?: boolean;
  setIsSubmittedSuccessfully?: (value: boolean) => void;
}
const ShareholderMatchingResult = ({
  data,
  loading,
}: DirectorMatchingResultProps) => {
  const cardContainerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [isOverflowing, setIsOverflowing] = useState(true);
  const { id: entityid } = useParams();

  const handleAddAsShareholder = async (data) => {
    // Strong sequencing: link shareholder to entity before navigating so entity-scoped fetch succeeds
    try {
      if (!data?.id) throw new Error("Shareholder id missing");

      // Fetch current shareholder (non-entity) to get entities[]
      const fetchResp: any = await apiService(
        API_ROUTES_V2.FETCH_SHAREHOLDER,
        "GET",
        {
          replace: { id: data.id, entity_id: entityid },
          suppressToast: true,
        },
      );
      const shareholder = fetchResp?.data || {};
      const rawEntities = shareholder.entities || [];
      const existingIds = rawEntities
        .map((e) => (typeof e === "string" ? e : e?.id))
        .filter(Boolean);

      if (!existingIds.includes(entityid)) {
        const newEntities = Array.from(new Set([...existingIds, entityid]));
        const requestBody = {
          // minimal required fields for update: preserve existing known fields
          type: shareholder.type,
          full_name: shareholder.full_name,
          email: shareholder.email,
          phone: shareholder.phone,
          phone_code: shareholder.phone_code,
          point_of_contact: shareholder.point_of_contact,
          contact_email: shareholder.contact_email,
          entities: newEntities,
          ...(shareholder.legal_name && { legal_name: shareholder.legal_name }),
          ...(shareholder.status && { status: shareholder.status }),
          ...(shareholder.country && { country: shareholder.country }),
        };

        await apiService(API_ROUTES_V2.UPDATE_SHAREHOLDER, "PUT", {
          replace: { id: data.id, entity_id: entityid },
          body: JSON.stringify(requestBody),
        });
      }

      // Navigate to registry tab after linking
      navigate(`/entity/${entityid}/share-holders/${data?.id}/registry`, {
        state: {
          shareholder: { ...shareholder, entities: [...existingIds, entityid] },
          entityid,
          skipBlocker: true,
        },
      });
    } catch (err) {
      // If linking fails, fall back to previous behaviour (open registry with skipEntityFetch)
      console.warn(
        "Pre-linking shareholder failed, falling back to previous navigation:",
        err,
      );
      navigate(`/entity/${entityid}/share-holders/${data?.id}/registry`, {
        state: {
          shareholder: data,
          skipBlocker: true,
          entityid: entityid,
          skipEntityFetch: true,
        },
      });
    }
  };

  useEffect(() => {
    const el = cardContainerRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [data, loading]);

  const handleCarouselDirection = (direction: "left" | "right") => {
    if (direction === "left") {
      cardContainerRef.current?.scrollBy({ left: -350, behavior: "smooth" });
    }
    if (direction === "right") {
      cardContainerRef.current?.scrollBy({ left: 350, behavior: "smooth" });
    }
  };
  const getTitle = () => {
    const allLinked = data.every((d) => Boolean(d.is_linked_to_entity));
    const noneLinked = data.every((d) => !d.is_linked_to_entity);
    console.log({ allLinked });

    if (allLinked) {
      return {
        title: "Oops! This shareholder is already linked to this entity.",
        description:
          "Review the info to avoid duplicates, or register if you believe there's an error.",
      };
    }

    if (noneLinked) {
      return {
        title: "Oops! This shareholder might already be registered.",
        description:
          "Review the info to avoid duplicates, or register if you believe there's an error.",
      };
    }
    return {
      title:
        "Oops! A shareholder with similar details already exists and seems to be linked to this entity.",
      description:
        "Review the info to avoid duplicates, or register if you believe there's an error.",
    };
  };
  return (
    <div className="director__matching-container">
      {data?.length > 0 && (
        <div className="heading-container">
          <h4 className="heading">{getTitle().title}</h4>
          <p className="desc">{getTitle().description}</p>
        </div>
      )}
      <div>
        {loading ? (
          <div className="cards-container" ref={cardContainerRef}>
            {[...Array(3)].map(() => {
              return (
                <div className="director__information__card">
                  <div className="flex align-items-center mb-3">
                    {/* Avatar */}
                    <Skeleton
                      shape="circle"
                      width="11rem"
                      height="7.5rem"
                      className="mr-2"
                    />
                    <div className="card__detail w-full">
                      <Skeleton width="60%" height="1rem" className="mb-2" />

                      <Skeleton width="40%" height="1rem" className="mb-2" />

                      <Skeleton width="50%" height="1rem" className="mb-2" />

                      <Skeleton width="70%" height="1rem" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Skeleton width="100%" height="2.5rem" />
                    <Skeleton width="100%" height="2.5rem" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="cards-container" ref={cardContainerRef}>
            {data.map((shareholder) => {
              return (
                <DuplicateInformationCard
                  duplicate={shareholder}
                  onAddAsDuplicate={handleAddAsShareholder}
                  type="shareholder"
                />
              );
            })}
          </div>
        )}

        {isOverflowing && (
          <div className="carousel-button-container">
            <Button
              type="button"
              outlined
              icon="pi pi-angle-left"
              onClick={() => handleCarouselDirection("left")}
            />
            <Button
              type="button"
              outlined
              icon="pi pi-angle-right"
              onClick={() => handleCarouselDirection("right")}
            />
          </div>
        )}
      </div>
    </div>
  );
};
export default ShareholderMatchingResult;
